import { useEffect, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { calculateDistance } from "../../../utils/googlePlaces";

function tokenizeName(s) {
  return new Set(
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 2)
  );
}

function normalizeText(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function nameSimilarity(a, b) {
  const na = normalizeText(a);
  const nb = normalizeText(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.88;
  const ta = tokenizeName(na);
  const tb = tokenizeName(nb);
  if (!ta.size || !tb.size) return 0;
  let intersection = 0;
  for (const t of ta) {
    if (tb.has(t)) intersection += 1;
  }
  return intersection / Math.max(ta.size, tb.size);
}

function nameIntersectionCount(a, b) {
  const ta = tokenizeName(a);
  const tb = tokenizeName(b);
  let inter = 0;
  for (const t of ta) {
    if (tb.has(t)) inter += 1;
  }
  return inter;
}

function computeDisgoCandidateScore(googlePlace, restaurant) {
  const ns = nameSimilarity(googlePlace?.name, restaurant?.name);
  let distanceScore = 0.5;
  const latA = googlePlace?.latitude;
  const lngA = googlePlace?.longitude;
  const latB = restaurant?.latitude;
  const lngB = restaurant?.longitude;
  if (
    typeof latA === "number" &&
    typeof lngA === "number" &&
    typeof latB === "number" &&
    typeof lngB === "number"
  ) {
    const km = calculateDistance(latA, lngA, latB, lngB);
    if (km < 0.15) distanceScore = 1;
    else if (km < 0.6) distanceScore = 0.85;
    else if (km < 1.5) distanceScore = 0.65;
    else if (km < 4) distanceScore = 0.45;
    else distanceScore = 0.2;
  }

  const sameGooglePlaceId =
    !!googlePlace?.id &&
    !!restaurant?.googlePlaceId &&
    String(googlePlace.id) === String(restaurant.googlePlaceId);

  // Name + distance + googlePlaceId only (DB address is deprecated).
  return ns * 0.78 + distanceScore * 0.22 + (sameGooglePlaceId ? 0.2 : 0);
}

function passesStrictMatchRules(googlePlace, restaurant, score) {
  const sameGooglePlaceId =
    !!googlePlace?.id &&
    !!restaurant?.googlePlaceId &&
    String(googlePlace.id) === String(restaurant.googlePlaceId);
  if (sameGooglePlaceId) return true;

  const ns = nameSimilarity(googlePlace?.name, restaurant?.name);
  const nameIntersections = nameIntersectionCount(googlePlace?.name, restaurant?.name);

  // Reject "Bombay Bistro" -> "Bombay Express" style false positives.
  // Require strong name overlap by similarity and shared terms.
  if (ns < 0.66 || nameIntersections < 2) return false;

  // Prefer nearby candidates when both have coordinates.
  const latA = googlePlace?.latitude;
  const lngA = googlePlace?.longitude;
  const latB = restaurant?.latitude;
  const lngB = restaurant?.longitude;
  if (
    typeof latA === "number" &&
    typeof lngA === "number" &&
    typeof latB === "number" &&
    typeof lngB === "number"
  ) {
    const km = calculateDistance(latA, lngA, latB, lngB);
    if (km > 4) return false;
  }

  return score >= 0.62;
}

async function matchGooglePlaceToDisgo(convex, googlePlace) {
  if (!googlePlace?.name) return null;
  const candidates =
    (await convex.query(api.getRestaurants.searchRestaurants, {
      searchText: googlePlace.name,
      limit: 8,
    })) || [];

  if (!Array.isArray(candidates) || candidates.length === 0) {
    return null;
  }

  let best = null;
  let bestScore = -1;
  for (const c of candidates) {
    const score = computeDisgoCandidateScore(googlePlace, c);
    if (score > bestScore && passesStrictMatchRules(googlePlace, c, score)) {
      best = c;
      bestScore = score;
    }
  }
  if (!best) return null;
  return best;
}

export function useHousePickDisgoMatches(googlePlaceMatches) {
  const convex = useConvex();
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (!Array.isArray(googlePlaceMatches) || googlePlaceMatches.length === 0) {
      setMatches([]);
      return;
    }

    let active = true;
    setMatches(
      googlePlaceMatches.map((m) => {
        if (!m || m.status === "loading") return { status: "loading" };
        if (m.status === "matched" && m.place) return { status: "loading" };
        return { status: "none" };
      })
    );

    (async () => {
      const next = await Promise.all(
        googlePlaceMatches.map(async (m) => {
          if (!m || m.status === "loading") return { status: "loading" };
          if (m.status !== "matched" || !m.place) return { status: "none" };
          try {
            const restaurant = await matchGooglePlaceToDisgo(convex, m.place);
            return restaurant ? { status: "matched", restaurant } : { status: "none" };
          } catch {
            return { status: "error" };
          }
        })
      );
      if (!active) return;
      setMatches(next);
    })();

    return () => {
      active = false;
    };
  }, [convex, googlePlaceMatches]);

  return matches;
}
